import React, { useEffect, useRef } from 'react';
import {
  Type,
  ChevronRight,
  BookOpen,
  Laugh,
  Zap,
  Sparkles,
} from 'lucide-react';

const TEMPLATE_CATEGORIES = [
  {
    name: '古诗词',
    icon: BookOpen,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    items: [
      '床前明月光，疑是地上霜。举头望明月，低头思故乡。',
      '春眠不觉晓，处处闻啼鸟。夜来风雨声，花落知多少。',
      '白日依山尽，黄河入海流。欲穷千里目，更上一层楼。',
      '锄禾日当午，汗滴禾下土。谁知盘中餐，粒粒皆辛苦。',
    ],
  },
  {
    name: '趣味绕口令',
    icon: Laugh,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    items: [
      '四是四，十是十，十四是十四，四十是四十。',
      '吃葡萄不吐葡萄皮，不吃葡萄倒吐葡萄皮。',
      '八百标兵奔北坡，炮兵并排北边跑。',
      '黑化肥发灰，灰化肥发黑。黑化肥发灰会挥发，灰化肥挥发会发黑。',
    ],
  },
  {
    name: '英语角',
    icon: Zap,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    items: [
      "Hello, everyone! Welcome to our AI Voice Lab. Let's explore the magic of speech synthesis together!",
      'The quick brown fox jumps over the lazy dog.',
      'Practice makes perfect. Keep trying every day!',
      'How much wood would a woodchuck chuck if a woodchuck could chuck wood?',
    ],
  },
  {
    name: '小故事',
    icon: Sparkles,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    items: [
      '从前有座山，山里有座庙，庙里有个老和尚在给小和尚讲故事。',
      '小兔子乖乖，把门儿开开，快点儿开开，我要进来！不开不开我不开，妈妈没回来，谁来也不开。',
      '三只小猪盖房子。老大盖了草房子，老二盖了木房子，老三盖了砖房子。大灰狼来了……',
      '在一个遥远的星球上，住着一只会说话的机器人。它最喜欢的事情，就是给星球上的小朋友们讲故事。',
    ],
  },
];

const MAX_CHARS = 100;

export function TextInputPanel({
  text,
  setText,
  selectedTemplateCat,
  setSelectedTemplateCat,
}) {
  const templateContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        templateContainerRef.current &&
        !templateContainerRef.current.contains(e.target)
      ) {
        setSelectedTemplateCat(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setSelectedTemplateCat]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm flex-1 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <Type size={18} className="text-purple-600" />
        <h3 className="font-bold text-gray-800">输入文字</h3>
        <span
          className={`text-xs ml-auto ${text.length > MAX_CHARS ? 'text-red-500' : 'text-gray-400'}`}
        >
          {text.length}/{MAX_CHARS} 字
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={MAX_CHARS}
        className="flex-1 w-full rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-800 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 resize-none transition min-h-[80px]"
        placeholder="在这里输入你想让AI朗读的内容..."
      />
      <div ref={templateContainerRef} className="flex flex-wrap gap-2 mt-3">
        {TEMPLATE_CATEGORIES.map((cat, idx) => {
          const Icon = cat.icon;
          const isOpen = selectedTemplateCat === idx;
          return (
            <div key={cat.name} className="relative">
              <button
                onClick={() => setSelectedTemplateCat(isOpen ? null : idx)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition ${cat.color} ${isOpen ? 'ring-2 ring-offset-1 ring-gray-200' : ''}`}
              >
                <Icon size={14} />
                {cat.name}
                <ChevronRight
                  size={14}
                  className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="absolute z-10 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-100 p-3">
                  <div className="space-y-2">
                    {cat.items.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setText(item);
                          setSelectedTemplateCat(null);
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition line-clamp-2"
                      >
                        {item.length > MAX_CHARS
                          ? item.slice(0, MAX_CHARS) + '…'
                          : item}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
